import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Sparkles, MapPin, Camera, ExternalLink, FileDown, Smartphone, CheckCircle, Trash2, FileText, Wand2 } from 'lucide-react'
import { Button, Input, Select, Card, CardHeader, CardTitle, ConfirmModal, Modal } from '../common'
import { ElementRating } from './ElementRating'
import { AccommodationMatrix } from './AccommodationMatrix'
import useSurveyStore from '../../stores/surveyStore'
import useSettingsStore from '../../stores/settingsStore'
import { surveyApi } from '../../services/apiService'
import { searchUKAddresses } from '../../services/geocodingService'
import { exportPDF, downloadPDF } from '../../services/pdfTemplateService'
import { generatePDFReport } from '../../services/pdfGenerator'
import { surveyDb } from '../../db/database'

// Auto-expanding textarea component
const AutoExpandTextarea = ({ value, onChange, placeholder, className = '', required = false }) => {
  const textareaRef = useRef(null)

  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to auto to get the correct scrollHeight
      textareaRef.current.style.height = 'auto'
      // Set height to scrollHeight to show all content
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px'
    }
  }, [value])

  // Check if field is empty/incomplete (only for required fields)
  const isEmpty = value !== undefined && (!value || value.toString().trim() === '')
  const showIncompleteStyle = isEmpty && required

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`w-full px-3 py-2 border-2 rounded-lg focus:ring-2 resize-none overflow-hidden ${showIncompleteStyle ? 'border-orange-500 focus:ring-orange-500 focus:border-orange-500' : 'border-slate-300 focus:ring-primary-500'} ${className}`}
      style={{ minHeight: '120px' }}
    />
  )
}

// Constants
const SURVEY_LEVELS = [
  { value: 1, label: 'Level 1', description: 'Basic visual inspection' },
  { value: 2, label: 'Level 2', description: 'Standard home survey' },
  { value: 3, label: 'Level 3', description: 'Full building survey' }
]

const PROPERTY_TYPES = [
  { value: '', label: 'Select...' },
  { value: 'house', label: 'House' },
  { value: 'flat', label: 'Flat' },
  { value: 'bungalow', label: 'Bungalow' },
  { value: 'terraced', label: 'Terraced' },
  { value: 'semi-detached', label: 'Semi-Detached' },
  { value: 'detached', label: 'Detached' }
]

const HEATING_TYPES = [
  { value: '', label: 'Select...' },
  { value: 'gas', label: 'Gas' },
  { value: 'electric', label: 'Electric' },
  { value: 'solid-fuel', label: 'Solid Fuel' },
  { value: 'oil', label: 'Oil' }
]

// Weather conditions options
const WEATHER_CONDITIONS = [
  { value: '', label: 'Select...' },
  { value: 'dry-clear', label: 'Dry and clear' },
  { value: 'dry-cloudy', label: 'Dry and cloudy' },
  { value: 'dry-overcast', label: 'Dry and overcast' },
  { value: 'light-rain', label: 'Light rain' },
  { value: 'heavy-rain', label: 'Heavy rain' },
  { value: 'recent-rain', label: 'Recent rain (ground wet)' },
  { value: 'frost-present', label: 'Frost present' },
  { value: 'snow-present', label: 'Snow present' }
]

// Property status options
const PROPERTY_STATUS = [
  { value: '', label: 'Select...' },
  { value: 'occupied-furnished', label: 'Occupied and furnished' },
  { value: 'occupied-unfurnished', label: 'Occupied and unfurnished' },
  { value: 'occupied-partially', label: 'Occupied and partially furnished' },
  { value: 'vacant-furnished', label: 'Vacant but furnished' },
  { value: 'vacant-unfurnished', label: 'Vacant and unfurnished' },
  { value: 'under-renovation', label: 'Under renovation' },
  { value: 'under-construction', label: 'Under construction' }
]

// Related party disclosure options
const RELATED_PARTY_DISCLOSURES = [
  {
    value: 'no-conflict',
    label: 'No conflicts of interest',
    text: 'We can confirm that, in accordance with the RICS Rules of Conduct, we are not aware of any conflicts of interest in accepting your instruction which may influence the recommendations contained in this report.'
  },
  {
    value: 'custom',
    label: 'Custom disclosure (edit below)',
    text: ''
  }
]

// Default surveyor details
const DEFAULT_SURVEYOR = {
  name: 'Christopher Baker MRICS BSc(Hons) FdSc',
  ricsNumber: '5634950',
  companyName: 'ClystCore Ltd'
}

// Default Section K details
const DEFAULT_SECTION_K = {
  phone: '01392 933392',
  email: 'info@clystcore.com',
  address: 'Queensgate House, 48 Queen Street, Exeter, Devon. EX4 3SR',
  qualifications: 'Full Member - Royal Institution of Chartered Surveyors',
  website: 'https://www.clystcore.co.uk/'
}

// Section D elements
const SECTION_D_ELEMENTS = [
  { code: 'd1', label: 'Chimney stacks' },
  { code: 'd2', label: 'Roof coverings' },
  { code: 'd3', label: 'Rainwater pipes and gutters' },
  { code: 'd4', label: 'Main walls' },
  { code: 'd5', label: 'Windows' },
  { code: 'd6', label: 'Outside doors (including patio doors)' },
  { code: 'd7', label: 'Conservatory and porches' },
  { code: 'd8', label: 'Other joinery and finishes' },
  { code: 'd9', label: 'Other' }
]

// Section E elements
const SECTION_E_ELEMENTS = [
  { code: 'e1', label: 'Roof structure' },
  { code: 'e2', label: 'Ceilings' },
  { code: 'e3', label: 'Walls and partitions' },
  { code: 'e4', label: 'Floors' },
  { code: 'e5', label: 'Fireplaces, chimney breasts and flues' },
  { code: 'e6', label: 'Built-in fittings (kitchen and other fittings)' },
  { code: 'e7', label: 'Woodwork (e.g., staircase joinery)' },
  { code: 'e8', label: 'Bathroom fittings' },
  { code: 'e9', label: 'Other' }
]

// Section F elements
const SECTION_F_ELEMENTS = [
  { code: 'f1', label: 'Electricity' },
  { code: 'f2', label: 'Gas/oil' },
  { code: 'f3', label: 'Water' },
  { code: 'f4', label: 'Heating' },
  { code: 'f5', label: 'Water heating' },
  { code: 'f6', label: 'Drainage' },
  { code: 'f7', label: 'Common services (flats/maisonettes)' }
]

// Section G elements
const SECTION_G_ELEMENTS = [
  { code: 'g1', label: 'Garage' },
  { code: 'g2', label: 'Permanent outbuildings and other structures' },
  { code: 'g3', label: 'Other' }
]

// Helper to generate random 5-digit report reference
function generateReportReference() {
  // Generate random 5-digit number (10000-99999)
  const randomNum = Math.floor(Math.random() * 90000) + 10000
  return String(randomNum)
}

// Helper to get category name from code
function getCategoryName(code) {
  const upperCode = code.toUpperCase()
  const allElements = [
    ...SECTION_D_ELEMENTS,
    ...SECTION_E_ELEMENTS,
    ...SECTION_F_ELEMENTS,
    ...SECTION_G_ELEMENTS
  ]

  const element = allElements.find(el => el.code.toUpperCase() === upperCode)
  return element ? element.label : code
}

export default function RicsSurveyForm({ initialData = null, onSubmit }) {
  const navigate = useNavigate()
  const { id } = useParams()
  const createSurvey = useSurveyStore(state => state.createSurvey)
  const updateSurvey = useSurveyStore(state => state.updateSurvey)
  const currentPhotos = useSurveyStore(state => state.currentPhotos)
  const addPhoto = useSurveyStore(state => state.addPhoto)
  const loadSurvey = useSurveyStore(state => state.loadSurvey)
  const deleteSurvey = useSurveyStore(state => state.deleteSurvey)
  const completeSurvey = useSurveyStore(state => state.completeSurvey)
  const defaultSurveyor = useSettingsStore(state => state.defaultSurveyor)
  const [nextReportRef, setNextReportRef] = useState('0120')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false)
  const [editingCategoryCode, setEditingCategoryCode] = useState(null)
  const [categoryNotes, setCategoryNotes] = useState('')
  const [savingCategoryNotes, setSavingCategoryNotes] = useState(false)
  const scrollPositionRef = useRef(0)

  // Helper to get photos for a specific category
  const getPhotosForCategory = useCallback((categoryCode) => {
    const filtered = currentPhotos.filter(photo =>
      photo.categoryCode?.toUpperCase() === categoryCode.toUpperCase()
    )
    return filtered
  }, [currentPhotos])

  // Initialize form data with all RICS fields
  const [formData, setFormData] = useState({
    // Basic survey info
    propertyName: initialData?.propertyName || '',
    address: initialData?.address || '',
    postcode: initialData?.postcode || '',
    surveyorName: initialData?.surveyorName || DEFAULT_SURVEYOR.name,
    surveyLevel: initialData?.surveyLevel || 1,

    // Section A: About the Inspection
    sectionAInspection: initialData?.sectionAInspection || {
      surveyorRicsNumber: DEFAULT_SURVEYOR.ricsNumber,
      companyName: DEFAULT_SURVEYOR.companyName,
      inspectionDate: new Date().toISOString().split('T')[0],
      reportReference: '',
      relatedPartyDisclosure: RELATED_PARTY_DISCLOSURES[0].text,
      weatherConditions: '',
      propertyStatus: ''
    },

    // Section B: Overall Opinion (Level 2 & 3)
    sectionBOpinion: initialData?.sectionBOpinion || '',
    sectionBDocuments: initialData?.sectionBDocuments || '',
    sectionBFurtherInvestigations: initialData?.sectionBFurtherInvestigations || '',

    // Section C: Property Details
    propertyType: initialData?.propertyType || '',
    sectionCDetails: initialData?.sectionCDetails || {
      yearBuilt: '',
      yearExtended: '',
      yearConverted: '',
      flatInfo: '',
      construction: '',
      energyRating: '',
      energyIssues: '', // Level 2 & 3
      otherEnergy: '', // Level 2 & 3
      accommodationMatrix: {},
      // Location and facilities (Level 2 & 3)
      locationGrounds: '',
      locationDescription: '',
      locationFacilities: '',
      localEnvironment: ''
    },

    // Mains services
    mainsServices: initialData?.mainsServices || {
      gas: false,
      electric: false,
      water: false,
      drainage: false
    },
    centralHeatingType: initialData?.centralHeatingType || '',
    otherEnergySources: initialData?.otherEnergySources || '',

    // Section D: Outside the Property
    sectionDOutside: initialData?.sectionDOutside || {
      limitations: '',
      ...Object.fromEntries(SECTION_D_ELEMENTS.map(e => [e.code, { rating: '', comment: '' }]))
    },

    // Section E: Inside the Property
    sectionEInside: initialData?.sectionEInside || {
      limitations: '',
      ...Object.fromEntries(SECTION_E_ELEMENTS.map(e => [e.code, { rating: '', comment: '' }]))
    },

    // Section F: Services
    sectionFServices: initialData?.sectionFServices || {
      limitations: '',
      ...Object.fromEntries(SECTION_F_ELEMENTS.map(e => [e.code, { rating: '', comment: '' }]))
    },

    // Section G: Grounds
    sectionGGrounds: initialData?.sectionGGrounds || {
      limitations: '',
      ...Object.fromEntries(SECTION_G_ELEMENTS.map(e => [e.code, { rating: '', comment: '' }]))
    },

    // Section H: Legal Issues
    sectionHLegal: initialData?.sectionHLegal || {
      h1Regulation: '',
      h2Guarantees: '',
      h3OtherMatters: ''
    },

    // Section I: Risks
    sectionIRisks: initialData?.sectionIRisks || {
      i1Building: '',
      i2Grounds: '',
      i3People: '',
      i4Other: ''
    },

    // Section J: Energy Matters (Level 3 only)
    sectionJEnergy: initialData?.sectionJEnergy || {
      j1Insulation: '',
      j2Heating: '',
      j3Lighting: '',
      j4Ventilation: '',
      j5General: ''
    },

    // Section K: Declaration
    sectionKDeclaration: initialData?.sectionKDeclaration || {
      phone: DEFAULT_SECTION_K.phone,
      address: DEFAULT_SECTION_K.address,
      qualifications: DEFAULT_SECTION_K.qualifications,
      email: DEFAULT_SECTION_K.email,
      website: DEFAULT_SECTION_K.website,
      clientName: '',
      reportDate: new Date().toISOString().split('T')[0],
      signatureConfirmed: false
    },

    ricsTemplateVersion: initialData?.ricsTemplateVersion || '2021'
  })

  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [relatedPartyOption, setRelatedPartyOption] = useState('no-conflict')
  const [addressSuggestions, setAddressSuggestions] = useState([])
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false)
  const [searchingAddress, setSearchingAddress] = useState(false)
  const [generatingSectionB, setGeneratingSectionB] = useState(false)
  const [generatingDocuments, setGeneratingDocuments] = useState(false)
  const [generatingFurtherInvestigations, setGeneratingFurtherInvestigations] = useState(false)
  const [generatingSectionH, setGeneratingSectionH] = useState(false)
  const [generatingSectionI, setGeneratingSectionI] = useState(false)
  const [generatingSectionJ, setGeneratingSectionJ] = useState(false)
  const [populatingSectionC, setPopulatingSectionC] = useState(false)
  const [generatingLocation, setGeneratingLocation] = useState(false)
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [generatingFullReport, setGeneratingFullReport] = useState(false)
  const [fullReportProgress, setFullReportProgress] = useState(null)
  const [reportGenerationStep, setReportGenerationStep] = useState(0)
  const [reportGenerationTotal, setReportGenerationTotal] = useState(10)
  const [autoSaving, setAutoSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState(null)
  const searchTimeoutRef = useRef(null)
  const addressInputRef = useRef(null)
  const autoSaveTimeoutRef = useRef(null)
  const isInitialMount = useRef(true)

  // Generate random report reference for new surveys
  useEffect(() => {
    if (!initialData) {
      const ref = generateReportReference()
      setNextReportRef(ref)
      setFormData(prev => ({
        ...prev,
        sectionAInspection: {
          ...prev.sectionAInspection,
          reportReference: ref
        }
      }))
    }
  }, [initialData])

  // Auto-save functionality - saves to backend WITHOUT updating Zustand store
  useEffect(() => {
    // Skip auto-save on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // Only auto-save for existing surveys (not new ones)
    if (!initialData?.id) {
      return
    }

    // Clear previous timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current)
    }

    // Set timeout for debounced save (2 seconds after last change)
    autoSaveTimeoutRef.current = setTimeout(async () => {
      setAutoSaving(true)
      try {
        // Save directly to backend API and IndexedDB without updating Zustand store
        // This prevents the re-render issue caused by store updates
        const remoteId = initialData.remoteId || initialData.id

        // First update local DB (sets synced: false)
        await surveyDb.update(initialData.id, formData)

        // Then sync to remote if available
        if (remoteId) {
          await surveyApi.update(remoteId, formData)
          // Mark as synced after successful remote update
          await surveyDb.markSynced(initialData.id, remoteId)
        }

        setLastSaved(new Date())
      } catch (error) {
        console.error('Auto-save failed:', error)
        // Don't show error to user - manual save is still available
      } finally {
        setAutoSaving(false)
      }
    }, 2000)

    // Cleanup timeout on unmount
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
    }
  }, [formData, initialData?.id, initialData?.remoteId])

  // Debounced address search
  const handleAddressSearch = async (query) => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    // Set timeout for debounced search
    searchTimeoutRef.current = setTimeout(async () => {
      if (query && query.trim().length >= 3) {
        setSearchingAddress(true)
        const results = await searchUKAddresses(query)
        setAddressSuggestions(results)
        setShowAddressSuggestions(results.length > 0)
        setSearchingAddress(false)
      } else {
        setAddressSuggestions([])
        setShowAddressSuggestions(false)
      }
    }, 500)
  }

  // Handle address selection from autocomplete
  const handleSelectAddress = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      address: suggestion.address,
      postcode: suggestion.postcode,
      propertyName: suggestion.address
    }))
    setShowAddressSuggestions(false)
    setAddressSuggestions([])
  }

  // Helper functions
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const updateNestedField = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }))
  }

  const updateElement = (section, elementCode, data) => {
    // Save scroll position before state update
    const savedScrollY = window.scrollY

    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [elementCode]: data
      }
    }))

    // Restore scroll position after React has re-rendered
    requestAnimationFrame(() => {
      window.scrollTo(0, savedScrollY)
    })
  }

  const validate = () => {
    const newErrors = {}
    if (!formData.propertyName.trim()) newErrors.propertyName = 'Property name is required'
    if (!formData.address.trim()) newErrors.address = 'Address is required'
    if (!formData.postcode.trim()) newErrors.postcode = 'Postcode is required'
    if (!formData.surveyorName.trim()) newErrors.surveyorName = 'Surveyor name is required'

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    setLoading(true)
    try {
      let survey
      if (initialData?.id) {
        // Update existing survey
        survey = await updateSurvey(initialData.id, formData)

        // Don't navigate - stay on the form so user can continue editing
        // The store has been updated, so if they navigate away and come back,
        // they'll see the updated data
        alert('Survey saved successfully!')
      } else {
        // Create new survey
        survey = await createSurvey(formData)
        if (onSubmit) {
          onSubmit(survey)
        } else {
          navigate(`/survey/${survey.id}`)
        }
      }
    } catch (error) {
      setErrors({ submit: error.message })
    } finally {
      setLoading(false)
    }
  }

  // Generate Section B overall opinion
  const handleGenerateSectionB = async () => {
    if (!initialData?.id) {
      alert('Please save the survey first before generating Section B')
      return
    }

    // Use remoteId for API call (surveys must be synced to server first)
    const surveyId = initialData.remoteId || initialData.id
    if (!initialData.remoteId) {
      alert('Please wait for the survey to sync to the server before generating Section B')
      return
    }

    setGeneratingSectionB(true)
    try {
      const { opinion } = await surveyApi.generateSectionB(surveyId)
      setFormData(prev => ({
        ...prev,
        sectionBOpinion: opinion
      }))
    } catch (error) {
      console.error('Failed to generate Section B:', error)
      alert(`Failed to generate Section B: ${error.message}`)
    } finally {
      setGeneratingSectionB(false)
    }
  }

  // Generate Documents to Request
  const handleGenerateDocuments = async () => {
    if (!initialData?.id) {
      alert('Please save the survey first before generating documents')
      return
    }

    // Use remoteId for API call (surveys must be synced to server first)
    const surveyId = initialData.remoteId || initialData.id
    if (!initialData.remoteId) {
      alert('Please wait for the survey to sync to the server before generating documents')
      return
    }

    setGeneratingDocuments(true)
    try {
      const { documents } = await surveyApi.generateDocuments(surveyId)
      setFormData(prev => ({
        ...prev,
        sectionBDocuments: documents
      }))
    } catch (error) {
      console.error('Failed to generate documents:', error)
      alert(`Failed to generate documents: ${error.message}`)
    } finally {
      setGeneratingDocuments(false)
    }
  }

  // Generate Further Investigations
  const handleGenerateFurtherInvestigations = async () => {
    if (!initialData?.id) {
      alert('Please save the survey first before generating Further Investigations')
      return
    }

    // Use remoteId for API call (surveys must be synced to server first)
    const surveyId = initialData.remoteId || initialData.id
    if (!initialData.remoteId) {
      alert('Please wait for the survey to sync to the server before generating Further Investigations')
      return
    }

    setGeneratingFurtherInvestigations(true)
    try {
      const { furtherInvestigations } = await surveyApi.generateFurtherInvestigations(surveyId)
      setFormData(prev => ({
        ...prev,
        sectionBFurtherInvestigations: furtherInvestigations
      }))
    } catch (error) {
      console.error('Failed to generate Further Investigations:', error)
      alert(`Failed to generate Further Investigations: ${error.message}`)
    } finally {
      setGeneratingFurtherInvestigations(false)
    }
  }

  // Generate Section H (Legal Issues)
  const handleGenerateSectionH = async () => {
    if (!initialData?.id) {
      alert('Please save the survey first before generating Section H')
      return
    }

    // Use remoteId for API call (surveys must be synced to server first)
    const surveyId = initialData.remoteId || initialData.id
    if (!initialData.remoteId) {
      alert('Please wait for the survey to sync to the server before generating Section H')
      return
    }

    setGeneratingSectionH(true)
    try {
      const { sectionH } = await surveyApi.generateSectionH(surveyId)
      setFormData(prev => ({
        ...prev,
        sectionHLegal: sectionH
      }))
    } catch (error) {
      console.error('Failed to generate Section H:', error)
      alert(`Failed to generate Section H: ${error.message}`)
    } finally {
      setGeneratingSectionH(false)
    }
  }

  // Generate Section I (Risks)
  const handleGenerateSectionI = async () => {
    if (!initialData?.id) {
      alert('Please save the survey first before generating Section I')
      return
    }

    // Use remoteId for API call (surveys must be synced to server first)
    const surveyId = initialData.remoteId || initialData.id
    if (!initialData.remoteId) {
      alert('Please wait for the survey to sync to the server before generating Section I')
      return
    }

    setGeneratingSectionI(true)
    try {
      const { sectionI } = await surveyApi.generateSectionI(surveyId)
      setFormData(prev => ({
        ...prev,
        sectionIRisks: sectionI
      }))
    } catch (error) {
      console.error('Failed to generate Section I:', error)
      alert(`Failed to generate Section I: ${error.message}`)
    } finally {
      setGeneratingSectionI(false)
    }
  }

  // Generate Section J (Energy Matters)
  const handleGenerateSectionJ = async () => {
    if (!initialData?.id) {
      alert('Please save the survey first before generating Section J')
      return
    }

    // Use remoteId for API call (surveys must be synced to server first)
    const surveyId = initialData.remoteId || initialData.id
    if (!initialData.remoteId) {
      alert('Please wait for the survey to sync to the server before generating Section J')
      return
    }

    setGeneratingSectionJ(true)
    try {
      const { sectionJ } = await surveyApi.generateSectionJ(surveyId)
      setFormData(prev => ({
        ...prev,
        sectionJEnergy: sectionJ
      }))
    } catch (error) {
      console.error('Failed to generate Section J:', error)
      alert(`Failed to generate Section J: ${error.message}`)
    } finally {
      setGeneratingSectionJ(false)
    }
  }

  // Populate Section C from EPC data
  const handlePopulateSectionC = async () => {
    if (!initialData?.id) {
      alert('Please save the survey first before populating Section C')
      return
    }

    // Use remoteId for API call (surveys must be synced to server first)
    const surveyId = initialData.remoteId || initialData.id
    if (!initialData.remoteId) {
      alert('Please wait for the survey to sync to the server before populating from EPC')
      return
    }

    setPopulatingSectionC(true)
    try {
      const { sectionC, epcData } = await surveyApi.populateSectionC(surveyId)

      // Update form data with EPC data
      setFormData(prev => ({
        ...prev,
        sectionCDetails: {
          ...prev.sectionCDetails,
          ...sectionC
        }
      }))

      // Show success message with EPC details
      alert(`Section C populated successfully!\n\nEPC Data Found:\n- Address: ${epcData.epcAddress}\n- Property Type: ${epcData.propertyType}\n- Construction Age: ${epcData.constructionAgeBand}\n- Floor Area: ${epcData.totalFloorArea} m²\n- EPC Lodged: ${epcData.epcLodgementDate}`)
    } catch (error) {
      console.error('Failed to populate Section C:', error)
      alert(`Failed to populate Section C: ${error.message}\n\nPlease ensure the postcode is correct and EPC data exists for this property.`)
    } finally {
      setPopulatingSectionC(false)
    }
  }

  // Generate Location and Facilities content with AI
  const handleGenerateLocation = async () => {
    if (!initialData?.id) {
      alert('Please save the survey first before generating location content')
      return
    }

    // Use remoteId for API call (surveys must be synced to server first)
    const surveyId = initialData.remoteId || initialData.id
    if (!initialData.remoteId) {
      alert('Please wait for the survey to sync to the server before generating location content')
      return
    }

    if (!formData.address || !formData.postcode) {
      alert('Please enter the property address and postcode first')
      return
    }

    setGeneratingLocation(true)
    try {
      const { location } = await surveyApi.generateLocationFacilities(surveyId, {
        address: formData.address,
        postcode: formData.postcode,
        propertyName: formData.propertyName
      })

      // Update form data with generated content
      setFormData(prev => ({
        ...prev,
        sectionCDetails: {
          ...prev.sectionCDetails,
          locationGrounds: location.grounds || prev.sectionCDetails.locationGrounds,
          locationDescription: location.location || prev.sectionCDetails.locationDescription,
          locationFacilities: location.facilities || prev.sectionCDetails.locationFacilities,
          localEnvironment: location.environment || prev.sectionCDetails.localEnvironment
        }
      }))

      alert('Location and Facilities content generated successfully!')
    } catch (error) {
      console.error('Failed to generate location content:', error)
      alert(`Failed to generate content: ${error.message}`)
    } finally {
      setGeneratingLocation(false)
    }
  }

  // Analyze section with AI (using all photos from elements in that section)
  const [analyzingSection, setAnalyzingSection] = useState(null)

  const handleAnalyzeSection = async (sectionCode, sectionElements) => {
    if (!initialData?.id) {
      alert('Please save the survey first before analyzing sections')
      return
    }

    // Get all category codes for this section (e.g., D1-D9)
    const categoryCodes = sectionElements.map(el => el.code.toUpperCase())
    const sectionPhotos = categoryCodes.flatMap(code => getPhotosForCategory(code))

    if (sectionPhotos.length === 0) {
      alert(`No photos found for Section ${sectionCode}. Please upload photos first.`)
      return
    }

    setAnalyzingSection(sectionCode)
    try {
      // Call API to analyze all categories in this section
      const results = await Promise.all(
        categoryCodes
          .filter(code => getPhotosForCategory(code).length > 0)
          .map(code => surveyApi.analyseCategory(initialData.id, code))
      )

      // Map results to element codes (results array matches filtered categoryCodes)
      const analysisByCode = {}
      const filteredCodes = categoryCodes.filter(code => getPhotosForCategory(code).length > 0)
      filteredCodes.forEach((code, index) => {
        if (results[index]?.analysis) {
          analysisByCode[code.toLowerCase()] = results[index].analysis
        }
      })

      // Update each element's comment field with its analysis
      const fieldName = `section${sectionCode}${sectionCode === 'D' ? 'Outside' : sectionCode === 'E' ? 'Inside' : sectionCode === 'F' ? 'Services' : 'Grounds'}`

      setFormData(prev => {
        const updatedSection = { ...prev[fieldName] }

        // Update each element with its analysis
        sectionElements.forEach(element => {
          const elementCode = element.code.toLowerCase()
          if (analysisByCode[elementCode]) {
            updatedSection[elementCode] = {
              ...updatedSection[elementCode],
              comment: analysisByCode[elementCode]
            }
          }
        })

        return {
          ...prev,
          [fieldName]: updatedSection
        }
      })

      alert(`Section ${sectionCode} analyzed successfully using ${sectionPhotos.length} photos!`)
    } catch (error) {
      console.error(`Failed to analyze Section ${sectionCode}:`, error)
      alert(`Failed to analyze Section ${sectionCode}: ${error.message}`)
    } finally {
      setAnalyzingSection(null)
    }
  }

  // Generate DOCX/PDF Report from template
  const handlePrintDraftReport = async () => {
    if (!initialData?.id) {
      alert('Please save the survey first before generating the report')
      return
    }

    setDownloadingPdf(true)
    try {
      // Build complete survey object with all form data
      const surveyForReport = {
        ...initialData,
        ...formData,
        createdAt: initialData.createdAt,
        updatedAt: new Date().toISOString()
      }

      // Generate DOCX from template
      const blob = await generatePDFReport(surveyForReport, currentPhotos, formData.surveyLevel || '3')

      // Download the report
      const filename = `${formData.propertyName.replace(/[^a-z0-9]/gi, '_')}_Survey_Report.docx`
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      alert('Report generated successfully! Opening in Word...')
    } catch (error) {
      console.error('Failed to generate report:', error)
      alert(`Failed to generate report: ${error.message}`)
    } finally {
      setDownloadingPdf(false)
    }
  }

  // Generate full draft report (all sections)
  const handleGenerateFullReport = async () => {
    if (!id) {
      alert('Please save the survey first')
      return
    }

    if (!window.confirm('Generate complete draft report? This will run AI analysis for all sections (C, D, E, F, G, H, I, J, and B). This may take 2-3 minutes and will use OpenAI credits. Continue?')) {
      return
    }

    setGeneratingFullReport(true)
    setReportGenerationStep(0)
    setReportGenerationTotal(10)

    // Step names for progress display
    const stepNames = [
      'Section C (EPC Data)',
      'Location & Facilities',
      'Section D Analysis',
      'Section E Analysis',
      'Section F Analysis',
      'Section G Analysis',
      'Section H (Legal Issues)',
      'Section I (Risks)',
      'Section J (Energy)',
      'Section B (Overall Opinion)'
    ]

    // Simulate progress updates (estimated timing: ~15 seconds per step)
    const progressInterval = setInterval(() => {
      setReportGenerationStep(prev => {
        const next = prev + 1
        if (next <= stepNames.length) {
          setFullReportProgress(`Step ${next}/${stepNames.length}: ${stepNames[next - 1]}`)
        }
        return Math.min(next, stepNames.length)
      })
    }, 15000) // Update every 15 seconds

    try {
      const result = await surveyApi.generateFullReport(id)

      // Clear interval when done
      clearInterval(progressInterval)
      setReportGenerationStep(stepNames.length)

      if (result.success) {
        setFullReportProgress(null)
        alert(`Full report generated successfully!\n\nCompleted ${result.steps.filter(s => s.status === 'completed').length} steps.\n\nPlease reload the survey to see all generated content.`)
        // Reload survey to get updated data
        await loadSurvey(id)
      } else {
        const errorCount = result.errors?.length || 0
        const successCount = result.steps?.filter(s => s.status === 'completed').length || 0
        alert(`Full report generation completed with ${errorCount} error(s).\n\n${successCount} steps succeeded.\n\nCheck console for details.`)
        console.error('Full report errors:', result.errors)
        // Still reload to get any partial results
        await loadSurvey(id)
      }
    } catch (error) {
      clearInterval(progressInterval)
      console.error('Failed to generate full report:', error)
      alert(`Failed to generate full report: ${error.message}`)
    } finally {
      setGeneratingFullReport(false)
      setFullReportProgress(null)
    }
  }

  // Handle complete survey
  const handleCompleteSurvey = async () => {
    await completeSurvey()
    setShowCompleteConfirm(false)
  }

  // Handle delete survey
  const handleDeleteSurvey = async () => {
    if (!id) return
    // Check if it's a UUID format (contains hyphens) - if so, keep as string
    const isUUID = id.includes('-')
    const surveyId = isUUID ? id : parseInt(id, 10)
    await deleteSurvey(surveyId)
    navigate('/surveys')
  }

  // Handle category notes
  const handleEditCategoryNotes = (categoryCode) => {
    const reviewData = initialData?.reviewData || {}
    const currentNotes = reviewData[categoryCode]?.comment || ''
    setEditingCategoryCode(categoryCode)
    setCategoryNotes(currentNotes)
  }

  const handleSaveCategoryNotes = async () => {
    if (!editingCategoryCode || !initialData) return
    setSavingCategoryNotes(true)
    try {
      const updatedReviewData = {
        ...(initialData.reviewData || {}),
        [editingCategoryCode]: {
          ...(initialData.reviewData?.[editingCategoryCode] || {}),
          comment: categoryNotes
        }
      }
      await updateSurvey(initialData.id, {
        reviewData: updatedReviewData
      })
      // Reload to get updated data
      await loadSurvey(initialData.id)
      setEditingCategoryCode(null)
      setCategoryNotes('')
    } catch (error) {
      console.error('Failed to save category notes:', error)
      alert('Failed to save notes. Please try again.')
    } finally {
      setSavingCategoryNotes(false)
    }
  }

  // Level-based visibility
  const showOverallOpinion = formData.surveyLevel >= 2
  const showLocationFacilities = formData.surveyLevel >= 2
  const showEnergyMattersSection = formData.surveyLevel === 3

  // Survey mode: new surveys only show Property Details tab
  // Existing surveys (with ID) show all tabs
  const isNewSurvey = !initialData?.id
  const showAllTabs = !isNewSurvey

  // Mini photo gallery component for element ratings
  const CategoryPhotos = ({ categoryCode }) => {
    const photos = getPhotosForCategory(categoryCode)
    const [uploading, setUploading] = useState(false)
    const [selectedPhoto, setSelectedPhoto] = useState(null)
    const [editingNotes, setEditingNotes] = useState(false)
    const [notesText, setNotesText] = useState('')
    const [savingNotes, setSavingNotes] = useState(false)
    const [drawingMode, setDrawingMode] = useState(false)
    const [drawingTool, setDrawingTool] = useState('pen') // 'pen', 'arrow', 'circle'
    const [showGallery, setShowGallery] = useState(null)
    const [selectedPhotos, setSelectedPhotos] = useState(new Set())
    const fileInputRef = useRef(null)
    const canvasRef = useRef(null)
    const imageRef = useRef(null)
    const [isDrawing, setIsDrawing] = useState(false)
    const [startPos, setStartPos] = useState(null)
    const updatePhotoNotes = useSurveyStore(state => state.updatePhotoNotes)
    const deletePhoto = useSurveyStore(state => state.deletePhoto)

    const handleFileSelect = async (e) => {
      const files = Array.from(e.target.files || [])
      if (files.length === 0 || !id) return

      // Save current scroll position
      const scrollY = window.scrollY

      setUploading(true)
      try {
        // Upload all selected files sequentially (saves locally, no sync)
        for (let i = 0; i < files.length; i++) {
          const file = files[i]
          await addPhoto({
            surveyId: id,
            categoryCode: categoryCode,
            blob: file
          })
        }

        // No need to reload - photos are added to state by addPhoto
        // User can click "Sync Now" when ready to upload to server

        // Restore scroll position
        window.scrollTo(0, scrollY)
      } catch (error) {
        console.error('Failed to add photo(s):', error)
        alert('Failed to add one or more photos. Please try again.')
      } finally {
        setUploading(false)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }
    }

    const handlePhotoClick = (photo) => {
      setSelectedPhoto(photo)
      setNotesText(photo.notes || '')
      setEditingNotes(false)
      setDrawingMode(false)
    }

    const handleSaveNotes = async () => {
      if (!selectedPhoto) return

      setSavingNotes(true)
      try {
        await updatePhotoNotes(selectedPhoto.id || selectedPhoto.localId, notesText)
        // updatePhotoNotes already updates the store state, no need to reload
        setSelectedPhoto({ ...selectedPhoto, notes: notesText })
        setEditingNotes(false)
      } catch (error) {
        console.error('Failed to save notes:', error)
        alert('Failed to save notes. Please try again.')
      } finally {
        setSavingNotes(false)
      }
    }

    // Initialize canvas when drawing mode is enabled
    useEffect(() => {
      if (drawingMode && canvasRef.current && imageRef.current) {
        const canvas = canvasRef.current
        const img = imageRef.current
        const rect = img.getBoundingClientRect()

        canvas.width = img.naturalWidth
        canvas.height = img.naturalHeight
        canvas.style.width = `${rect.width}px`
        canvas.style.height = `${rect.height}px`

        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0)
      }
    }, [drawingMode])

    const getCanvasCoordinates = (e) => {
      const canvas = canvasRef.current
      const rect = canvas.getBoundingClientRect()
      const scaleX = canvas.width / rect.width
      const scaleY = canvas.height / rect.height

      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY
      }
    }

    const handleCanvasMouseDown = (e) => {
      if (!drawingMode) return
      setIsDrawing(true)
      const pos = getCanvasCoordinates(e)
      setStartPos(pos)

      const ctx = canvasRef.current.getContext('2d')
      ctx.strokeStyle = '#FF0000'
      ctx.lineWidth = 3
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'

      if (drawingTool === 'pen') {
        ctx.beginPath()
        ctx.moveTo(pos.x, pos.y)
      }
    }

    const handleCanvasMouseMove = (e) => {
      if (!isDrawing || !drawingMode) return
      const pos = getCanvasCoordinates(e)
      const ctx = canvasRef.current.getContext('2d')

      if (drawingTool === 'pen') {
        ctx.lineTo(pos.x, pos.y)
        ctx.stroke()
      }
    }

    const handleCanvasMouseUp = (e) => {
      if (!isDrawing || !drawingMode) return
      setIsDrawing(false)

      const pos = getCanvasCoordinates(e)
      const ctx = canvasRef.current.getContext('2d')

      if (drawingTool === 'arrow') {
        drawArrow(ctx, startPos.x, startPos.y, pos.x, pos.y)
      } else if (drawingTool === 'circle') {
        drawCircle(ctx, startPos.x, startPos.y, pos.x, pos.y)
      }
    }

    const drawArrow = (ctx, fromX, fromY, toX, toY) => {
      const headLen = 20
      const angle = Math.atan2(toY - fromY, toX - fromX)

      ctx.beginPath()
      ctx.moveTo(fromX, fromY)
      ctx.lineTo(toX, toY)
      ctx.stroke()

      ctx.beginPath()
      ctx.moveTo(toX, toY)
      ctx.lineTo(toX - headLen * Math.cos(angle - Math.PI / 6), toY - headLen * Math.sin(angle - Math.PI / 6))
      ctx.moveTo(toX, toY)
      ctx.lineTo(toX - headLen * Math.cos(angle + Math.PI / 6), toY - headLen * Math.sin(angle + Math.PI / 6))
      ctx.stroke()
    }

    const drawCircle = (ctx, x1, y1, x2, y2) => {
      const radius = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2))
      ctx.beginPath()
      ctx.arc(x1, y1, radius, 0, 2 * Math.PI)
      ctx.stroke()
    }

    const handleClearDrawing = () => {
      if (canvasRef.current && imageRef.current) {
        const ctx = canvasRef.current.getContext('2d')
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)
        ctx.drawImage(imageRef.current, 0, 0)
      }
    }

    const handleSaveAnnotated = () => {
      if (!canvasRef.current) return

      const annotatedImage = canvasRef.current.toDataURL('image/jpeg', 0.95)
      // TODO: Save annotated image - for now just show success message
      alert('Drawing saved! (Feature coming soon: save annotated version)')
      setDrawingMode(false)
    }

    return (
      <>
        <div className="mt-2 border-t border-slate-200 pt-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Camera className="w-4 h-4 text-slate-500" />
              <span className="text-sm text-slate-600">
                {photos.length} photo{photos.length !== 1 ? 's' : ''}
              </span>
            </div>
            {id && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="text-xs px-2 py-1 bg-primary-50 text-primary-600 rounded hover:bg-primary-100 disabled:opacity-50 flex items-center gap-1"
              >
                {uploading ? (
                  <>
                    <div className="spinner w-3 h-3" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Camera className="w-3 h-3" />
                    Add Photo
                  </>
                )}
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              capture="environment"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
          {photos.length > 0 && (
            <>
              <div className="grid grid-cols-4 gap-2">
                {photos.slice(0, 4).map((photo, idx) => (
                  <div
                    key={photo.id || idx}
                    className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => handlePhotoClick(photo)}
                  >
                    <img
                      src={photo.thumbnailUrl || photo.imageUrl}
                      alt={`${categoryCode} ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {photo.notes && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs p-1 truncate">
                        {photo.notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {photos.length > 4 && (
                <button
                  type="button"
                  onClick={() => setShowGallery(categoryCode)}
                  className="text-xs text-primary-600 hover:text-primary-700 underline mt-1"
                >
                  +{photos.length - 4} more photo{photos.length - 4 !== 1 ? 's' : ''} - View all
                </button>
              )}
            </>
          )}
        </div>

        {/* Photo Modal */}
        {selectedPhoto && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedPhoto(null)}
          >
            <div
              className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold">Photo Details</h3>
                <div className="flex items-center gap-2">
                  {!drawingMode && (
                    <button type="button"
                      onClick={() => setDrawingMode(true)}
                      className="px-3 py-1.5 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                      Annotate
                    </button>
                  )}
                  <button type="button"
                    onClick={() => setSelectedPhoto(null)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-4">
                {/* Drawing Toolbar */}
                {drawingMode && (
                  <div className="mb-4 p-3 bg-slate-100 rounded-lg flex items-center gap-3">
                    <span className="text-sm font-medium text-slate-700">Drawing Tools:</span>
                    <div className="flex gap-2">
                      <button type="button"
                        onClick={() => setDrawingTool('pen')}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          drawingTool === 'pen'
                            ? 'bg-red-500 text-white'
                            : 'bg-white text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        Pen
                      </button>
                      <button type="button"
                        onClick={() => setDrawingTool('arrow')}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          drawingTool === 'arrow'
                            ? 'bg-red-500 text-white'
                            : 'bg-white text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        Arrow
                      </button>
                      <button type="button"
                        onClick={() => setDrawingTool('circle')}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          drawingTool === 'circle'
                            ? 'bg-red-500 text-white'
                            : 'bg-white text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        Circle
                      </button>
                    </div>
                    <div className="flex-1" />
                    <button type="button"
                      onClick={handleClearDrawing}
                      className="px-3 py-2 bg-white text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200"
                    >
                      Clear
                    </button>
                    <button type="button"
                      onClick={handleSaveAnnotated}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                    >
                      Done
                    </button>
                    <button type="button"
                      onClick={() => {
                        setDrawingMode(false)
                        handleClearDrawing()
                      }}
                      className="px-3 py-2 bg-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-400"
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {/* Image with Canvas Overlay */}
                <div className="relative inline-block">
                  <img
                    ref={imageRef}
                    src={selectedPhoto.imageUrl}
                    alt="Full size"
                    className="w-full h-auto rounded-lg mb-4"
                    style={{ display: drawingMode ? 'none' : 'block' }}
                  />
                  {drawingMode && (
                    <canvas
                      ref={canvasRef}
                      onMouseDown={handleCanvasMouseDown}
                      onMouseMove={handleCanvasMouseMove}
                      onMouseUp={handleCanvasMouseUp}
                      onMouseLeave={() => setIsDrawing(false)}
                      className="w-full h-auto rounded-lg mb-4 cursor-crosshair border-2 border-red-500"
                    />
                  )}
                </div>

                {!drawingMode && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-slate-700">Comments</label>
                      {!editingNotes && (
                        <button type="button"
                          onClick={() => setEditingNotes(true)}
                          className="text-xs text-primary-600 hover:text-primary-700"
                        >
                          Edit
                        </button>
                      )}
                    </div>

                    {editingNotes ? (
                      <div className="space-y-2">
                        <textarea
                          value={notesText}
                          onChange={(e) => setNotesText(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          rows={3}
                          placeholder="Add comments about this photo..."
                        />
                        <div className="flex gap-2">
                          <button type="button"
                            onClick={handleSaveNotes}
                            disabled={savingNotes}
                            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                          >
                            {savingNotes ? 'Saving...' : 'Save'}
                          </button>
                          <button type="button"
                            onClick={() => {
                              setEditingNotes(false)
                              setNotesText(selectedPhoto.notes || '')
                            }}
                            className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-600">
                        {selectedPhoto.notes || 'No comments yet'}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Photo Gallery Modal with Multi-Select */}
        {showGallery === categoryCode && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
            onClick={() => {
              setShowGallery(null)
              setSelectedPhotos(new Set())
            }}
          >
            <div
              className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Gallery Header */}
              <div className="p-4 border-b border-slate-200 sticky top-0 bg-white z-10">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">
                      {categoryCode.toUpperCase()} Photos ({photos.length})
                    </h3>
                    {selectedPhotos.size > 0 && (
                      <p className="text-sm text-slate-600 mt-1">
                        {selectedPhotos.size} photo{selectedPhotos.size !== 1 ? 's' : ''} selected
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedPhotos.size > 0 && (
                      <>
                        <button type="button"
                          onClick={() => setSelectedPhotos(new Set())}
                          className="px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
                        >
                          Clear Selection
                        </button>
                        <button type="button"
                          onClick={async () => {
                            if (confirm(`Delete ${selectedPhotos.size} selected photo${selectedPhotos.size !== 1 ? 's' : ''}?`)) {
                              for (const photoId of selectedPhotos) {
                                await deletePhoto(photoId)
                              }
                              setSelectedPhotos(new Set())
                              await loadSurvey(id)
                            }
                          }}
                          className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                          Delete Selected
                        </button>
                      </>
                    )}
                    <button type="button"
                      onClick={() => {
                        setShowGallery(null)
                        setSelectedPhotos(new Set())
                      }}
                      className="p-2 hover:bg-slate-100 rounded-lg"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Gallery Grid */}
              <div className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {photos.map((photo, idx) => {
                    const isSelected = selectedPhotos.has(photo.id)
                    return (
                      <div
                        key={photo.id || idx}
                        className={`relative aspect-square rounded-lg overflow-hidden bg-slate-100 cursor-pointer transition-all ${
                          isSelected ? 'ring-4 ring-primary-500' : 'hover:opacity-80'
                        }`}
                        onClick={() => {
                          const newSelected = new Set(selectedPhotos)
                          if (isSelected) {
                            newSelected.delete(photo.id)
                          } else {
                            newSelected.add(photo.id)
                          }
                          setSelectedPhotos(newSelected)
                        }}
                      >
                        {/* Selection Checkbox */}
                        <div className="absolute top-2 left-2 z-10">
                          <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                            isSelected
                              ? 'bg-primary-600 border-primary-600'
                              : 'bg-white border-slate-300'
                          }`}>
                            {isSelected && (
                              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </div>

                        {/* Photo Image */}
                        <img
                          src={photo.thumbnailUrl || photo.imageUrl}
                          alt={`${categoryCode} ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />

                        {/* Photo Notes */}
                        {photo.notes && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white text-xs p-2">
                            <p className="line-clamp-2">{photo.notes}</p>
                          </div>
                        )}

                        {/* View Button */}
                        <button type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedPhoto(photo)
                            setShowGallery(null)
                          }}
                          className="absolute top-2 right-2 p-1.5 bg-white rounded-lg shadow-lg hover:bg-slate-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    )
  }

  // Build tabs array
  const tabs = [
    // Tab 1: Property & Inspection Details
    {
      label: 'Property Details',
      content: (
        <div className="space-y-6">
          {/* Basic Property Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Property Name"
              name="propertyName"
              value={formData.propertyName}
              onChange={handleChange}
              error={errors.propertyName}
              required
            />
            <div className="relative">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Address <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  ref={addressInputRef}
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={(e) => {
                    handleChange(e)
                    handleAddressSearch(e.target.value)
                  }}
                  onFocus={() => {
                    if (addressSuggestions.length > 0) {
                      setShowAddressSuggestions(true)
                    }
                  }}
                  onBlur={() => {
                    // Delay to allow clicking on suggestions
                    setTimeout(() => setShowAddressSuggestions(false), 200)
                  }}
                  placeholder="Start typing address..."
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:ring-2 ${
                    errors.address ? 'border-red-500 focus:ring-red-500' : !formData.address ? 'border-orange-500 focus:ring-orange-500' : 'border-slate-300 focus:ring-primary-500'
                  }`}
                  required
                />
                {searchingAddress && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="spinner w-4 h-4" />
                  </div>
                )}
              </div>
              {errors.address && (
                <p className="text-red-500 text-sm mt-1">{errors.address}</p>
              )}

              {/* Address Suggestions Dropdown */}
              {showAddressSuggestions && addressSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                  {addressSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleSelectAddress(suggestion)}
                      className="w-full text-left px-4 py-3 hover:bg-slate-50 focus:bg-slate-50 border-b border-slate-100 last:border-b-0 transition-colors"
                    >
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-900 truncate">{suggestion.address}</div>
                          <div className="text-sm text-slate-500 truncate">{suggestion.fullAddress}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Input
              label="Postcode"
              name="postcode"
              value={formData.postcode}
              onChange={handleChange}
              error={errors.postcode}
              required
            />
            <Select
              label="Property Type"
              name="propertyType"
              value={formData.propertyType}
              onChange={handleChange}
              options={PROPERTY_TYPES}
            />
          </div>

          {/* Survey Level */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Survey Level <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-3">
              {SURVEY_LEVELS.map((level) => (
                <button
                  key={level.value}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, surveyLevel: level.value }))}
                  className={`
                    p-3 rounded-lg border-2 text-center transition-all
                    ${formData.surveyLevel === level.value
                      ? 'border-primary-700 bg-primary-50 text-primary-700'
                      : 'border-slate-200 hover:border-slate-300 text-slate-600'
                    }
                  `}
                >
                  <div className="font-semibold">{level.label}</div>
                  <div className="text-xs mt-1 opacity-75">{level.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Section A: About the Inspection */}
          <div className="border-t pt-6 mt-6">
            <h3 className="text-2xl font-semibold text-slate-900 mb-6">
              Section A: About the Inspection
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Surveyor's Name"
                value={formData.surveyorName}
                onChange={(e) => setFormData(prev => ({ ...prev, surveyorName: e.target.value }))}
                required
              />
              <Input
                label="Surveyor's RICS Number"
                value={formData.sectionAInspection.surveyorRicsNumber}
                onChange={(e) => updateNestedField('sectionAInspection', 'surveyorRicsNumber', e.target.value)}
              />
              <Input
                label="Company Name"
                value={formData.sectionAInspection.companyName}
                onChange={(e) => updateNestedField('sectionAInspection', 'companyName', e.target.value)}
              />
              <Input
                label="Date of Inspection"
                type="date"
                value={formData.sectionAInspection.inspectionDate}
                onChange={(e) => updateNestedField('sectionAInspection', 'inspectionDate', e.target.value)}
              />
              <Input
                label="Report Reference Number"
                value={formData.sectionAInspection.reportReference}
                onChange={(e) => updateNestedField('sectionAInspection', 'reportReference', e.target.value)}
                readOnly
              />
              <Select
                label="Weather Conditions"
                value={formData.sectionAInspection.weatherConditions}
                onChange={(e) => updateNestedField('sectionAInspection', 'weatherConditions', e.target.value)}
                options={WEATHER_CONDITIONS}
              />
            </div>
            <div className="mt-4">
              <Select
                label="Property Status at Inspection"
                value={formData.sectionAInspection.propertyStatus}
                onChange={(e) => updateNestedField('sectionAInspection', 'propertyStatus', e.target.value)}
                options={PROPERTY_STATUS}
              />
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Related Party Disclosure
              </label>
              <Select
                value={relatedPartyOption}
                onChange={(e) => {
                  const option = e.target.value
                  setRelatedPartyOption(option)
                  const disclosure = RELATED_PARTY_DISCLOSURES.find(d => d.value === option)
                  if (disclosure && disclosure.text) {
                    updateNestedField('sectionAInspection', 'relatedPartyDisclosure', disclosure.text)
                  }
                }}
                options={RELATED_PARTY_DISCLOSURES.map(d => ({ value: d.value, label: d.label }))}
              />
              <AutoExpandTextarea
                value={formData.sectionAInspection.relatedPartyDisclosure}
                onChange={(e) => updateNestedField('sectionAInspection', 'relatedPartyDisclosure', e.target.value)}
                placeholder="Disclosure statement..."
                className="mt-2"
              />
            </div>
          </div>
        </div>
      )
    }
  ]

  // Only add remaining tabs if survey exists (not new)
  if (showAllTabs) {
    // Only add Overall Opinion tab for Level 2/3
    if (showOverallOpinion) {
      tabs.push({
        label: 'Section B: Summary',
        content: (
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    Section B: Overall Opinion of Property
                  </h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Provide a summary of the property's condition and key findings (Level {formData.surveyLevel} survey).
                  </p>
                </div>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleGenerateSectionB}
                  loading={generatingSectionB}
                  disabled={!initialData?.id}
                  title={!initialData?.id ? 'Save survey first to use this feature' : generatingSectionB ? 'Generating...' : 'Generate overall opinion based on survey findings'}
                >
                  <Sparkles className="w-5 h-5" />
                </Button>
              </div>
              <AutoExpandTextarea
                value={formData.sectionBOpinion}
                onChange={(e) => setFormData(prev => ({ ...prev, sectionBOpinion: e.target.value }))}
                placeholder="Overall opinion of the property condition, major findings, recommendations..."
              />
              {generatingSectionB && (
                <p className="text-xs text-slate-500 mt-2">
                  Analyzing survey findings and generating professional opinion...
                </p>
              )}
            </div>

            {/* Documents to Request Table */}
            <div className="border-t pt-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-md font-semibold text-slate-900">
                  Documents we may suggest you request before you sign contracts
                </h4>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleGenerateDocuments}
                  loading={generatingDocuments}
                  disabled={!initialData?.id}
                  title={!initialData?.id ? 'Save survey first to use this feature' : generatingDocuments ? 'Generating...' : 'Generate recommended documents using AI'}
                >
                  <Sparkles className="w-5 h-5" />
                </Button>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <AutoExpandTextarea
                  value={formData.sectionBDocuments || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, sectionBDocuments: e.target.value }))}
                  placeholder="List documents to request (one per line)&#10;Example:&#10;Building regulations approval certificate for extension&#10;Guarantees for damp-proof course work&#10;Planning permission for loft conversion"
                  rows={6}
                />
                <p className="text-xs text-slate-500 mt-2">
                  Enter each document on a new line. These will appear in the generated report.
                </p>
              </div>
              {generatingDocuments && (
                <p className="text-xs text-slate-500 mt-2">
                  Analyzing survey findings and generating document recommendations...
                </p>
              )}
            </div>

            {/* Condition Ratings Tables - Auto-generated based on ratings */}
            <div className="border-t pt-6">
              <h4 className="text-md font-semibold text-slate-900 mb-3">
                Condition Ratings
              </h4>
              <p className="text-sm text-slate-600 mb-4">
                These tables are automatically generated based on category ratings. They will appear in the report.
              </p>

              {/* Preview of auto-generated tables */}
              <div className="space-y-4 bg-slate-50 p-4 rounded-lg">
                {(() => {
                  const rating3 = []
                  const rating2 = []
                  const rating1 = []
                  const ratingNI = []

                  // Collect all ratings from sections D-G
                  const sections = [
                    { key: 'sectionDOutside', codes: ['d1', 'd2', 'd3', 'd4', 'd5', 'd6', 'd7', 'd8', 'd9'] },
                    { key: 'sectionEInside', codes: ['e1', 'e2', 'e3', 'e4', 'e5', 'e6', 'e7', 'e8', 'e9'] },
                    { key: 'sectionFServices', codes: ['f1', 'f2', 'f3', 'f4', 'f5', 'f6', 'f7'] },
                    { key: 'sectionGGrounds', codes: ['g1', 'g2', 'g3'] }
                  ]

                  sections.forEach(section => {
                    section.codes.forEach(code => {
                      const data = formData[section.key]?.[code]
                      if (data?.rating) {
                        const categoryName = getCategoryName(code)
                        const item = { code: code.toUpperCase(), name: categoryName }

                        if (data.rating === '3') rating3.push(item)
                        else if (data.rating === '2') rating2.push(item)
                        else if (data.rating === '1') rating1.push(item)
                        else if (data.rating === 'NI') ratingNI.push(item)
                      }
                    })
                  })

                  return (
                    <>
                      {rating3.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center text-white font-bold">3</div>
                            <div className="text-sm font-semibold text-slate-900">Elements that require urgent attention</div>
                          </div>
                          <div className="bg-white border rounded text-xs">
                            <div className="grid grid-cols-2 font-semibold border-b bg-slate-100 p-2">
                              <div>Element no.</div>
                              <div>Element name</div>
                            </div>
                            {rating3.map((item, idx) => (
                              <div key={idx} className="grid grid-cols-2 p-2 border-b last:border-b-0">
                                <div>{item.code}</div>
                                <div>{item.name}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {rating2.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold">2</div>
                            <div className="text-sm font-semibold text-slate-900">Elements that require attention but are not serious or urgent</div>
                          </div>
                          <div className="bg-white border rounded text-xs">
                            <div className="grid grid-cols-2 font-semibold border-b bg-slate-100 p-2">
                              <div>Element no.</div>
                              <div>Element name</div>
                            </div>
                            {rating2.map((item, idx) => (
                              <div key={idx} className="grid grid-cols-2 p-2 border-b last:border-b-0">
                                <div>{item.code}</div>
                                <div>{item.name}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {rating1.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center text-white font-bold">1</div>
                            <div className="text-sm font-semibold text-slate-900">Elements with no current issues</div>
                          </div>
                          <div className="bg-white border rounded text-xs">
                            <div className="grid grid-cols-2 font-semibold border-b bg-slate-100 p-2">
                              <div>Element no.</div>
                              <div>Element name</div>
                            </div>
                            {rating1.map((item, idx) => (
                              <div key={idx} className="grid grid-cols-2 p-2 border-b last:border-b-0">
                                <div>{item.code}</div>
                                <div>{item.name}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {ratingNI.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 rounded-full bg-slate-400 flex items-center justify-center text-white font-bold text-xs">NI</div>
                            <div className="text-sm font-semibold text-slate-900">Elements not inspected</div>
                          </div>
                          <div className="bg-white border rounded text-xs">
                            <div className="grid grid-cols-2 font-semibold border-b bg-slate-100 p-2">
                              <div>Element no.</div>
                              <div>Element name</div>
                            </div>
                            {ratingNI.map((item, idx) => (
                              <div key={idx} className="grid grid-cols-2 p-2 border-b last:border-b-0">
                                <div>{item.code}</div>
                                <div>{item.name}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {rating3.length === 0 && rating2.length === 0 && rating1.length === 0 && ratingNI.length === 0 && (
                        <p className="text-sm text-slate-500 italic">No categories have been rated yet.</p>
                      )}
                    </>
                  )
                })()}
              </div>
            </div>

            {/* Further Investigations */}
            <div className="border-t pt-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-md font-semibold text-slate-900">
                  Further Investigations
                </h4>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleGenerateFurtherInvestigations}
                  loading={generatingFurtherInvestigations}
                  disabled={!initialData?.id}
                  title={!initialData?.id ? 'Save survey first to use this feature' : generatingFurtherInvestigations ? 'Generating...' : 'Generate recommended investigations using AI'}
                >
                  <Sparkles className="w-5 h-5" />
                </Button>
              </div>
              <p className="text-sm text-slate-600 mb-4">
                Recommended further investigations based on the survey findings
              </p>
              <div className="bg-slate-50 p-4 rounded-lg">
                <AutoExpandTextarea
                  value={formData.sectionBFurtherInvestigations || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, sectionBFurtherInvestigations: e.target.value }))}
                  placeholder="List recommended further investigations (one per line)&#10;Example:&#10;Structural engineer inspection of cracking in main walls&#10;CCTV drainage survey to assess condition of drains&#10;Electrical safety inspection and testing"
                  rows={6}
                />
                <p className="text-xs text-slate-500 mt-2">
                  Enter each investigation on a new line. These will appear in the generated report.
                </p>
              </div>
              {generatingFurtherInvestigations && (
                <p className="text-xs text-slate-500 mt-2">
                  Analyzing survey findings and generating investigation recommendations...
                </p>
              )}
            </div>
          </div>
        )
      })
    }

  // Section C: About the Property
  tabs.push({
    label: 'Section C: Property',
    content: (
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              Section C: About the Property
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              Property details, construction, and energy information
            </p>
          </div>
          <Button
            type="button"
            variant="primary"
            onClick={handlePopulateSectionC}
            loading={populatingSectionC}
            disabled={!initialData?.id}
            title={!initialData?.id ? 'Save survey first to use this feature' : populatingSectionC ? 'Loading...' : 'Auto-populate fields from EPC data'}
          >
            <Sparkles className="w-5 h-5" />
          </Button>
        </div>

        {populatingSectionC && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
            Fetching property data from Energy Performance Certificate database...
          </div>
        )}

        {/* Construction and Years */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Input
            label="Year Built (Approx.)"
            value={formData.sectionCDetails.yearBuilt}
            onChange={(e) => updateNestedField('sectionCDetails', 'yearBuilt', e.target.value)}
            placeholder="e.g., 1920"
          />
          <Input
            label="Year Extended (Approx.)"
            value={formData.sectionCDetails.yearExtended}
            onChange={(e) => updateNestedField('sectionCDetails', 'yearExtended', e.target.value)}
            placeholder="e.g., 1985"
          />
          <Input
            label="Year Converted (Approx.)"
            value={formData.sectionCDetails.yearConverted}
            onChange={(e) => updateNestedField('sectionCDetails', 'yearConverted', e.target.value)}
            placeholder="e.g., 2005"
          />
        </div>

        {/* Flat Info */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Information Relevant to Flats/Maisonettes
          </label>
          <AutoExpandTextarea
            value={formData.sectionCDetails.flatInfo}
            onChange={(e) => updateNestedField('sectionCDetails', 'flatInfo', e.target.value)}
            placeholder="e.g., Floor level, lease details, service charges..."
          />
        </div>

        {/* Construction */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Construction (walls, roof, floors)
          </label>
          <AutoExpandTextarea
            value={formData.sectionCDetails.construction}
            onChange={(e) => updateNestedField('sectionCDetails', 'construction', e.target.value)}
            placeholder="Describe construction materials and methods..."
          />
        </div>

        {/* Accommodation Matrix */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Accommodation (Number of Rooms by Floor)
          </label>
          <AccommodationMatrix
            value={formData.sectionCDetails.accommodationMatrix}
            onChange={(matrix) => updateNestedField('sectionCDetails', 'accommodationMatrix', matrix)}
          />
        </div>

        {/* Energy Efficiency */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Energy Efficiency Rating (from EPC)"
            value={formData.sectionCDetails.energyRating}
            onChange={(e) => updateNestedField('sectionCDetails', 'energyRating', e.target.value)}
            placeholder="e.g., C (69)"
          />
        </div>

        {showLocationFacilities && (
          <>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Issues Relating to Energy Efficiency (Level 2 & 3)
              </label>
              <AutoExpandTextarea
                value={formData.sectionCDetails.energyIssues}
                onChange={(e) => updateNestedField('sectionCDetails', 'energyIssues', e.target.value)}
                placeholder="Describe any energy efficiency concerns..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Other Energy Matters (Level 2 & 3)
              </label>
              <AutoExpandTextarea
                value={formData.sectionCDetails.otherEnergy}
                onChange={(e) => updateNestedField('sectionCDetails', 'otherEnergy', e.target.value)}
                placeholder="Other energy-related matters..."
              />
            </div>
          </>
        )}

        {/* Mains Services */}
        <div className="border-t pt-6">
          <label className="block text-sm font-medium text-slate-700 mb-3">
            Mains Services Available
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['gas', 'electric', 'water', 'drainage'].map(service => (
              <label key={service} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.mainsServices[service] || false}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      mainsServices: {
                        ...prev.mainsServices,
                        [service]: e.target.checked
                      }
                    }))
                  }}
                  className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-slate-300 rounded"
                />
                <span className="text-sm text-slate-700 capitalize">{service}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Central Heating Type */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Central Heating Type
          </label>
          <Select
            value={formData.centralHeatingType}
            onChange={(e) => setFormData(prev => ({ ...prev, centralHeatingType: e.target.value }))}
            options={HEATING_TYPES}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Other Services or Energy Sources
          </label>
          <input
            type="text"
            value={formData.otherEnergySources}
            onChange={(e) => setFormData(prev => ({ ...prev, otherEnergySources: e.target.value }))}
            placeholder="e.g., Solar panels, heat pump..."
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* Location and Facilities (Level 2 & 3) */}
        {showLocationFacilities && (
          <div className="border-t pt-6 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-md font-semibold text-slate-900">Location and Facilities (Level 2 & 3)</h4>
              <Button
                type="button"
                variant="primary"
                onClick={handleGenerateLocation}
                loading={generatingLocation}
                disabled={!initialData?.remoteId}
                title={
                  !initialData?.id
                    ? 'Save survey first to use this feature'
                    : !initialData?.remoteId
                    ? 'Survey must sync to server first (refresh the page if online)'
                    : generatingLocation ? 'Generating...' : 'Generate location and facilities content using AI'
                }
              >
                <Sparkles className="w-5 h-5" />
              </Button>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Grounds</label>
              <AutoExpandTextarea
                value={formData.sectionCDetails.locationGrounds}
                onChange={(e) => updateNestedField('sectionCDetails', 'locationGrounds', e.target.value)}
                placeholder="Describe the grounds..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Location</label>
              <AutoExpandTextarea
                value={formData.sectionCDetails.locationDescription}
                onChange={(e) => updateNestedField('sectionCDetails', 'locationDescription', e.target.value)}
                placeholder="Describe the location..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Facilities</label>
              <AutoExpandTextarea
                value={formData.sectionCDetails.locationFacilities}
                onChange={(e) => updateNestedField('sectionCDetails', 'locationFacilities', e.target.value)}
                placeholder="Describe local facilities..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Local Environment</label>
              <AutoExpandTextarea
                value={formData.sectionCDetails.localEnvironment}
                onChange={(e) => updateNestedField('sectionCDetails', 'localEnvironment', e.target.value)}
                placeholder="Describe the local environment..."
              />
            </div>
          </div>
        )}
      </div>
    )
  })

  // Section D: Outside the Property
  tabs.push({
    label: 'Section D: Outside',
    content: (
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">
            Section D: Outside the Property
          </h3>
          {id && (
            <Button
              type="button"
              variant="primary"
              onClick={() => handleAnalyzeSection('D', SECTION_D_ELEMENTS)}
              loading={analyzingSection === 'D'}
              title={analyzingSection === 'D' ? 'Analyzing...' : 'Analyze Section with AI'}
            >
              <Sparkles className="w-5 h-5" />
            </Button>
          )}
        </div>

        <div className="space-y-4">
          {SECTION_D_ELEMENTS.map(element => {
            const categoryCode = element.code.toUpperCase()
            const hasNotes = initialData?.reviewData?.[categoryCode]?.comment
            return (
              <div key={element.code}>
                <div className="flex items-start gap-2 mb-2">
                  <div className="flex-1">
                    <ElementRating
                      code={categoryCode}
                      label={element.label}
                      rating={formData.sectionDOutside[element.code]?.rating || ''}
                      comment={formData.sectionDOutside[element.code]?.comment || ''}
                      onChange={(rating, comment) => updateElement('sectionDOutside', element.code, { rating, comment })}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditCategoryNotes(categoryCode)}
                    className={`mt-6 ${hasNotes ? 'bg-orange-500 text-white border-orange-500 hover:bg-orange-600 hover:border-orange-600' : ''}`}
                    title={hasNotes ? "Edit category notes" : "Add category notes"}
                  >
                    <FileText className="w-4 h-4" />
                  </Button>
                </div>
                <CategoryPhotos categoryCode={categoryCode} />
              </div>
            )
          })}
        </div>
      </div>
    )
  })

  // Section E: Inside the Property
  tabs.push({
    label: 'Section E: Inside',
    content: (
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">
            Section E: Inside the Property
          </h3>
          {id && (
            <Button
              type="button"
              variant="primary"
              onClick={() => handleAnalyzeSection('E', SECTION_E_ELEMENTS)}
              loading={analyzingSection === 'E'}
              title={analyzingSection === 'E' ? 'Analyzing...' : 'Analyze Section with AI'}
            >
              <Sparkles className="w-5 h-5" />
            </Button>
          )}
        </div>

        <div className="space-y-4">
          {SECTION_E_ELEMENTS.map(element => {
            const categoryCode = element.code.toUpperCase()
            const hasNotes = initialData?.reviewData?.[categoryCode]?.comment
            return (
              <div key={element.code}>
                <div className="flex items-start gap-2 mb-2">
                  <div className="flex-1">
                    <ElementRating
                      code={categoryCode}
                      label={element.label}
                      rating={formData.sectionEInside[element.code]?.rating || ''}
                      comment={formData.sectionEInside[element.code]?.comment || ''}
                      onChange={(rating, comment) => updateElement('sectionEInside', element.code, { rating, comment })}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditCategoryNotes(categoryCode)}
                    className={`mt-6 ${hasNotes ? 'bg-orange-500 text-white border-orange-500 hover:bg-orange-600 hover:border-orange-600' : ''}`}
                    title={hasNotes ? "Edit category notes" : "Add category notes"}
                  >
                    <FileText className="w-4 h-4" />
                  </Button>
                </div>
                <CategoryPhotos categoryCode={categoryCode} />
              </div>
            )
          })}
        </div>
      </div>
    )
  })

  // Section F: Services
  tabs.push({
    label: 'Section F: Services',
    content: (
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">
            Section F: Services
          </h3>
          {id && (
            <Button
              type="button"
              variant="primary"
              onClick={() => handleAnalyzeSection('F', SECTION_F_ELEMENTS)}
              loading={analyzingSection === 'F'}
              title={analyzingSection === 'F' ? 'Analyzing...' : 'Analyze Section with AI'}
            >
              <Sparkles className="w-5 h-5" />
            </Button>
          )}
        </div>

        <div className="space-y-4">
          {SECTION_F_ELEMENTS.map(element => {
            const categoryCode = element.code.toUpperCase()
            const hasNotes = initialData?.reviewData?.[categoryCode]?.comment
            return (
              <div key={element.code}>
                <div className="flex items-start gap-2 mb-2">
                  <div className="flex-1">
                    <ElementRating
                      code={categoryCode}
                      label={element.label}
                      rating={formData.sectionFServices[element.code]?.rating || ''}
                      comment={formData.sectionFServices[element.code]?.comment || ''}
                      onChange={(rating, comment) => updateElement('sectionFServices', element.code, { rating, comment })}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditCategoryNotes(categoryCode)}
                    className={`mt-6 ${hasNotes ? 'bg-orange-500 text-white border-orange-500 hover:bg-orange-600 hover:border-orange-600' : ''}`}
                    title={hasNotes ? "Edit category notes" : "Add category notes"}
                  >
                    <FileText className="w-4 h-4" />
                  </Button>
                </div>
                <CategoryPhotos categoryCode={categoryCode} />
              </div>
            )
          })}
        </div>
      </div>
    )
  })

  // Section G: Grounds
  tabs.push({
    label: 'Section G: Grounds',
    content: (
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">
            Section G: Grounds (including shared areas for flats)
          </h3>
          {id && (
            <Button
              type="button"
              variant="primary"
              onClick={() => handleAnalyzeSection('G', SECTION_G_ELEMENTS)}
              loading={analyzingSection === 'G'}
              title={analyzingSection === 'G' ? 'Analyzing...' : 'Analyze Section with AI'}
            >
              <Sparkles className="w-5 h-5" />
            </Button>
          )}
        </div>

        <div className="space-y-4">
          {SECTION_G_ELEMENTS.map(element => {
            const categoryCode = element.code.toUpperCase()
            const hasNotes = initialData?.reviewData?.[categoryCode]?.comment
            return (
              <div key={element.code}>
                <div className="flex items-start gap-2 mb-2">
                  <div className="flex-1">
                    <ElementRating
                      code={categoryCode}
                      label={element.label}
                      rating={formData.sectionGGrounds[element.code]?.rating || ''}
                      comment={formData.sectionGGrounds[element.code]?.comment || ''}
                      onChange={(rating, comment) => updateElement('sectionGGrounds', element.code, { rating, comment })}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditCategoryNotes(categoryCode)}
                    className={`mt-6 ${hasNotes ? 'bg-orange-500 text-white border-orange-500 hover:bg-orange-600 hover:border-orange-600' : ''}`}
                    title={hasNotes ? "Edit category notes" : "Add category notes"}
                  >
                    <FileText className="w-4 h-4" />
                  </Button>
                </div>
                <CategoryPhotos categoryCode={categoryCode} />
              </div>
            )
          })}
        </div>
      </div>
    )
  })

  // Section H: Legal Issues
  tabs.push({
    label: 'Section H: Legal',
    content: (
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">
            Section H: Issues for Your Legal Advisers
          </h3>
          {id && (
            <Button
              type="button"
              variant="primary"
              onClick={handleGenerateSectionH}
              loading={generatingSectionH}
              disabled={!initialData?.id}
              title={!initialData?.id ? 'Save survey first to use this feature' : generatingSectionH ? 'Generating...' : 'Generate legal issues based on survey findings'}
            >
              <Sparkles className="w-5 h-5" />
            </Button>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            H1: Regulation
          </label>
          <AutoExpandTextarea
            value={formData.sectionHLegal.h1Regulation}
            onChange={(e) => updateNestedField('sectionHLegal', 'h1Regulation', e.target.value)}
            placeholder="Issues related to regulation and compliance..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            H2: Guarantees
          </label>
          <AutoExpandTextarea
            value={formData.sectionHLegal.h2Guarantees}
            onChange={(e) => updateNestedField('sectionHLegal', 'h2Guarantees', e.target.value)}
            placeholder="Information about guarantees and warranties..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            H3: Other Matters
          </label>
          <AutoExpandTextarea
            value={formData.sectionHLegal.h3OtherMatters}
            onChange={(e) => updateNestedField('sectionHLegal', 'h3OtherMatters', e.target.value)}
            placeholder="Other legal matters for consideration..."
          />
        </div>
      </div>
    )
  })

  // Section I: Risks
  tabs.push({
    label: 'Section I: Risks',
    content: (
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">
            Section I: Risks
          </h3>
          {id && (
            <Button
              type="button"
              variant="primary"
              onClick={handleGenerateSectionI}
              loading={generatingSectionI}
              disabled={!initialData?.id}
              title={!initialData?.id ? 'Save survey first to use this feature' : generatingSectionI ? 'Generating...' : 'Generate risks assessment based on survey findings'}
            >
              <Sparkles className="w-5 h-5" />
            </Button>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            I1: Risks to the Building
          </label>
          <AutoExpandTextarea
            value={formData.sectionIRisks.i1Building}
            onChange={(e) => updateNestedField('sectionIRisks', 'i1Building', e.target.value)}
            placeholder="Identify any risks to the building..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            I2: Risks to the Grounds
          </label>
          <AutoExpandTextarea
            value={formData.sectionIRisks.i2Grounds}
            onChange={(e) => updateNestedField('sectionIRisks', 'i2Grounds', e.target.value)}
            placeholder="Identify any risks to the grounds..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            I3: Risks to People
          </label>
          <AutoExpandTextarea
            value={formData.sectionIRisks.i3People}
            onChange={(e) => updateNestedField('sectionIRisks', 'i3People', e.target.value)}
            placeholder="Identify any risks to people..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            I4: Other Risks or Hazards
          </label>
          <AutoExpandTextarea
            value={formData.sectionIRisks.i4Other}
            onChange={(e) => updateNestedField('sectionIRisks', 'i4Other', e.target.value)}
            placeholder="Identify any other risks or hazards..."
          />
        </div>
      </div>
    )
  })

  // Section J: Energy Matters (Level 3 only)
  if (showEnergyMattersSection) {
    tabs.push({
      label: 'Section J: Energy',
      content: (
        <div className="space-y-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">
              Section J: Energy Matters (Level 3 Only)
            </h3>
            {id && (
              <Button
                type="button"
                variant="primary"
                onClick={handleGenerateSectionJ}
                loading={generatingSectionJ}
                disabled={!initialData?.id}
                title={!initialData?.id ? 'Save survey first to use this feature' : generatingSectionJ ? 'Generating...' : 'Generate energy assessment based on survey findings and EPC data'}
              >
                <Sparkles className="w-5 h-5" />
              </Button>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              J1: Insulation
            </label>
            <AutoExpandTextarea
              value={formData.sectionJEnergy.j1Insulation}
              onChange={(e) => updateNestedField('sectionJEnergy', 'j1Insulation', e.target.value)}
              placeholder="Assessment of insulation in walls, roof, floors, and windows..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              J2: Heating
            </label>
            <AutoExpandTextarea
              value={formData.sectionJEnergy.j2Heating}
              onChange={(e) => updateNestedField('sectionJEnergy', 'j2Heating', e.target.value)}
              placeholder="Analysis of heating system efficiency and controls..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              J3: Lighting
            </label>
            <AutoExpandTextarea
              value={formData.sectionJEnergy.j3Lighting}
              onChange={(e) => updateNestedField('sectionJEnergy', 'j3Lighting', e.target.value)}
              placeholder="Assessment of lighting efficiency and natural light..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              J4: Ventilation
            </label>
            <AutoExpandTextarea
              value={formData.sectionJEnergy.j4Ventilation}
              onChange={(e) => updateNestedField('sectionJEnergy', 'j4Ventilation', e.target.value)}
              placeholder="Analysis of ventilation systems and air quality..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              J5: General
            </label>
            <AutoExpandTextarea
              value={formData.sectionJEnergy.j5General}
              onChange={(e) => updateNestedField('sectionJEnergy', 'j5General', e.target.value)}
              placeholder="General energy matters and recommendations..."
            />
          </div>
        </div>
      )
    })
  }

  // Section K: Declaration
  tabs.push({
    label: 'Section K: Declaration',
    content: (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">
          Section K: Surveyor's Declaration
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Phone Number"
            type="tel"
            value={formData.sectionKDeclaration.phone}
            onChange={(e) => updateNestedField('sectionKDeclaration', 'phone', e.target.value)}
            placeholder="+44 20 1234 5678"
          />
          <Input
            label="Email Address"
            type="email"
            value={formData.sectionKDeclaration.email}
            onChange={(e) => updateNestedField('sectionKDeclaration', 'email', e.target.value)}
            placeholder="surveyor@example.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Surveyor's Address
          </label>
          <AutoExpandTextarea
            value={formData.sectionKDeclaration.address}
            onChange={(e) => updateNestedField('sectionKDeclaration', 'address', e.target.value)}
            placeholder="Full business address..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Qualifications
          </label>
          <AutoExpandTextarea
            value={formData.sectionKDeclaration.qualifications}
            onChange={(e) => updateNestedField('sectionKDeclaration', 'qualifications', e.target.value)}
            placeholder="Professional qualifications and memberships..."
          />
        </div>

        <div>
          <Input
            label="Website"
            type="url"
            value={formData.sectionKDeclaration.website}
            onChange={(e) => updateNestedField('sectionKDeclaration', 'website', e.target.value)}
            placeholder="https://www.example.com"
          />
        </div>

        <div>
          <Input
            label="Client's Name"
            value={formData.sectionKDeclaration.clientName}
            onChange={(e) => updateNestedField('sectionKDeclaration', 'clientName', e.target.value)}
            placeholder="Full name of client"
          />
        </div>

        <div>
          <Input
            label="Report Date"
            type="date"
            value={formData.sectionKDeclaration.reportDate}
            onChange={(e) => updateNestedField('sectionKDeclaration', 'reportDate', e.target.value)}
          />
        </div>

        <div className="border-t pt-6">
          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.sectionKDeclaration.signatureConfirmed}
              onChange={(e) => updateNestedField('sectionKDeclaration', 'signatureConfirmed', e.target.checked)}
              className="w-5 h-5 text-primary-600 focus:ring-primary-500 border-slate-300 rounded mt-0.5"
            />
            <span className="text-sm text-slate-700">
              I confirm that I have inspected the property and prepared this report, and that the contents are true to the best of my knowledge and belief.
            </span>
          </label>
        </div>
      </div>
    )
  })
  } // End showAllTabs

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle>
              {initialData ? 'Edit Survey' : 'New Survey'} - RICS Level {formData.surveyLevel}
            </CardTitle>
            {initialData?.id && (
              <div className="text-xs text-slate-500">
                {autoSaving ? (
                  <span className="flex items-center gap-1">
                    <div className="spinner w-3 h-3" />
                    Saving...
                  </span>
                ) : lastSaved ? (
                  <span>Saved {new Date(lastSaved).toLocaleTimeString()}</span>
                ) : null}
              </div>
            )}
          </div>
          {id && (
            <div className="flex items-center gap-2">
              {/* Mobile View Toggle - Only show on tablets (640px-1024px) */}
              {typeof window !== 'undefined' && window.innerWidth >= 640 && window.innerWidth < 1024 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/survey/${id}/photos`)}
                  className="flex items-center gap-2"
                  title="Switch to Mobile View"
                >
                  <Smartphone className="w-4 h-4" />
                  Mobile View
                </Button>
              )}
              <Button
                type="button"
                variant="primary"
                onClick={() => navigate(`/survey/${id}/photos`)}
                title="Photos"
              >
                <Camera className="w-5 h-5" />
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handleGenerateFullReport}
                disabled={generatingFullReport}
                title={generatingFullReport ? fullReportProgress || 'Generating...' : 'Generate Full Draft Report (AI)'}
                className="bg-purple-600 hover:bg-purple-700 text-white border-purple-600"
              >
                <Wand2 className="w-5 h-5" />
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handlePrintDraftReport}
                disabled={downloadingPdf}
                title={downloadingPdf ? 'Generating...' : 'Print Draft Report'}
              >
                <FileDown className="w-5 h-5" />
              </Button>
              {initialData?.status === 'in-progress' && currentPhotos.length > 0 && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowCompleteConfirm(true)}
                  title="Mark as Complete"
                >
                  <CheckCircle className="w-5 h-5" />
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/survey/${id}/review`)}
                title="Review & Finalize"
              >
                <FileText className="w-5 h-5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowDeleteConfirm(true)}
                className="text-red-600 hover:bg-red-50"
                title="Delete"
              >
                <Trash2 className="w-5 h-5" />
              </Button>
            </div>
          )}
        </div>
      </CardHeader>

      <form onSubmit={handleSubmit} onKeyDown={(e) => {
        // Prevent Enter key from submitting the form
        if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
          e.preventDefault()
        }
      }} className="p-6">
        {/* Single scrolling page with all sections */}
        <div className="space-y-8">
          {tabs.map((tab, index) => (
            <div key={index} className="scroll-mt-20">
              {/* Section Header */}
              <div className="pb-4 mb-6 border-b-2 border-primary-200">
                <h2 className="text-2xl font-bold text-slate-900">{tab.label}</h2>
              </div>

              {/* Section Content */}
              <div className="mb-8">
                {tab.content}
              </div>
            </div>
          ))}
        </div>

        {errors.submit && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg mt-6">
            <p className="text-red-600 text-sm">{errors.submit}</p>
          </div>
        )}

        <div className="flex gap-4 pt-6 border-t mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            className="flex-1"
          >
            {initialData ? 'Save Changes' : 'Create Survey'}
          </Button>
        </div>
      </form>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteSurvey}
        title="Delete Survey"
        message={`Are you sure you want to delete "${initialData?.propertyName}"? This will also delete all ${currentPhotos.length} photos. This action cannot be undone.`}
        confirmText="Delete Survey"
        variant="danger"
      />

      {/* Complete Confirmation Modal */}
      <ConfirmModal
        isOpen={showCompleteConfirm}
        onClose={() => setShowCompleteConfirm(false)}
        onConfirm={handleCompleteSurvey}
        title="Mark Survey as Complete"
        message="Are you sure you want to mark this survey as complete? You can still edit it later if needed."
        confirmText="Mark as Complete"
        variant="primary"
      />

      {/* Category Notes Modal */}
      <Modal
        isOpen={!!editingCategoryCode}
        onClose={() => !savingCategoryNotes && setEditingCategoryCode(null)}
        title={`Category Notes - ${editingCategoryCode}`}
        size="md"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setEditingCategoryCode(null)}
              disabled={savingCategoryNotes}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveCategoryNotes}
              disabled={savingCategoryNotes}
            >
              {savingCategoryNotes ? 'Saving...' : 'Save Notes'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Add notes for this category. These notes will be included in the AI analysis and PDF report.
          </p>
          <textarea
            value={categoryNotes}
            onChange={(e) => setCategoryNotes(e.target.value)}
            placeholder="Add category-level notes here (e.g., 'All windows are double-glazed and in good condition', 'Several areas of damp noted throughout')..."
            rows={6}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            disabled={savingCategoryNotes}
          />
        </div>
      </Modal>

      {/* Full Report Generation Progress Modal */}
      <Modal
        isOpen={generatingFullReport}
        onClose={() => {}} // Prevent closing during generation
        title="Generating Full Draft Report"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Please wait while AI generates all sections of your survey report. This process may take 2-3 minutes.
          </p>

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-slate-700">
              <span>{fullReportProgress || 'Initializing...'}</span>
              <span>{reportGenerationStep}/{reportGenerationTotal}</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
              <div
                className="bg-purple-600 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${(reportGenerationStep / reportGenerationTotal) * 100}%` }}
              />
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-slate-500 mt-4">
            <div className="spinner w-4 h-4" />
            <span>Do not close this window...</span>
          </div>
        </div>
      </Modal>
    </Card>
  )
}
